/**
 * DISC-7's two **lifecycle** re-evaluation triggers, in one place for both capture surfaces.
 *
 * §5.5, ratified: *"The trigger set is `page entry · visibilitychange · pageshow` — all three, on
 * both surfaces."* Page entry belongs to each surface (a mount, a pager change), so it stays in the
 * hooks. The two lifecycle events are identical on both surfaces and live here.
 *
 * ── Why this is a module rather than two copies of an effect ─────────────────
 *
 * It was two copies, and they drifted: U-9b's locked board listened for both events, U-8's Discovery
 * listened for `visibilitychange` only, and the design recorded the gap as a known defect. The
 * asymmetry that hid it is §5.5's own: **DISC-7's negative holds under any subset of triggers** —
 * fewer triggers move fewer rows — so every no-move test passed with the trigger missing. Only the
 * positive failed, and it failed as a page that had *not sorted yet*, which reads as normal.
 *
 * A shared registrar is the mechanism, not the discipline: there is now one trigger set, so one
 * surface cannot carry fewer triggers than the other. That is also what makes the set assertable —
 * see `settleTriggers.test.ts`, which drives real events at real `EventTarget`s.
 *
 * ── `pageshow` is not decoration, and it is not `document`'s ─────────────────
 *
 * §2.6, §5.5 and §13.7 all turn on the same platform fact: **WebKit can restore a page from the
 * back/forward cache without firing `visibilitychange` at all.** A board restored from bfcache with
 * only a `visibilitychange` listener never re-evaluates the gate, and — because iOS throttles and
 * suspends timers in a backgrounded page — the quiet-window timeout armed before the phone went into
 * a pocket may never fire either. Between them that is a page that stays permanently unsorted.
 *
 * The event is registered on **`window`**, deliberately and not interchangeably: page transition
 * events are fired *at* `Window`, which is the top of the propagation path, so a `document` listener
 * is never on it and never runs. Verified in WebKit rather than assumed (§0.3's rule: a platform
 * claim lives in a table row or in a runtime probe, never in a comment that nothing evaluates).
 *
 * ── Re-evaluate, never bypass ───────────────────────────────────────────────
 *
 * Both handlers call the surface's `settle` scheduler, which asks `reorderAllowed` against the same
 * gate a timeout would. §5.5's ratified divergence is explicit that a surface-entry **bypass** breaks
 * DISC-7's unconditional second sentence: *"entering a page, returning from background, or restoring
 * from bfcache **within** `REORDER_QUIET_MS` of a tap MUST NOT move any row."* Nothing here decides
 * whether to move a row; it decides only when to ask.
 */

export interface SettleTriggerOptions {
  /**
   * What `visibilitychange` is attached to. Defaults to `document`, which is the only correct answer
   * in a browser and the reason it is a parameter: the Node suite has no `document`, and a trigger
   * set whose one wiring obligation no test can exercise is the trigger set that goes missing.
   */
  readonly visibilityTarget?: EventTarget;
  /** What `pageshow` is attached to. Defaults to `window` — see the module note; not `document`. */
  readonly pageTarget?: EventTarget;
  /**
   * Whether the page is currently visible.
   *
   * A parameter for the same reason, and for a second: the negative *"a `visibilitychange` toward
   * **hidden** re-evaluates nothing"* is only assertable if a test can present a hidden page, which
   * no bare `EventTarget` can do.
   */
  readonly isVisible?: () => boolean;
}

/** Visible unless a real `document` says otherwise. A missing document is not a hidden page. */
function documentIsVisible(): boolean {
  return typeof document === 'undefined' || document.visibilityState === 'visible';
}

/**
 * Registers both lifecycle triggers. Returns the disposer; calling it removes both.
 *
 * `settle` is the surface's own scheduler (`scheduleSettle`), never an "apply the order" call.
 */
export function registerSettleTriggers(
  settle: () => void,
  options: SettleTriggerOptions = {},
): () => void {
  const visibilityTarget = options.visibilityTarget ?? document;
  const pageTarget = options.pageTarget ?? window;
  const isVisible = options.isVisible ?? documentIsVisible;

  // Only toward visible. The `visibilitychange` that fires as the phone goes into a pocket has
  // nothing to settle for — nobody is looking — and scheduling there would arm a timer in the
  // background, which is what NFR-5 spends §2.6 and §12.1a refusing.
  const onVisibilityChange = (): void => {
    if (isVisible()) settle();
  };

  // `pageshow` carries no visibility question: the page is being shown. Gating it on `isVisible`
  // would reintroduce the bug this trigger exists to fix on any runtime that restores from bfcache
  // before updating `visibilityState`.
  const onPageShow = (): void => {
    settle();
  };

  visibilityTarget.addEventListener('visibilitychange', onVisibilityChange);
  pageTarget.addEventListener('pageshow', onPageShow);

  return () => {
    visibilityTarget.removeEventListener('visibilitychange', onVisibilityChange);
    pageTarget.removeEventListener('pageshow', onPageShow);
  };
}
